"use client";

import { Box, Typography, Paper, Grid, Divider, Button } from "@mui/material";
import { CheckCircle, ErrorOutline, OpenInNew } from "@mui/icons-material";

export default function PaymentStatus({ success, amount, details, userData }) {
  return (
    <Box
      sx={{
        maxWidth: 500,
        mx: "auto",
        textAlign: "center",
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Status Icon & Message */}
      <Box>
        {success ? (
          <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 1 }} />
        ) : (
          <ErrorOutline sx={{ fontSize: 64, color: "error.main", mb: 1 }} />
        )}

        <Typography variant="h5" fontWeight="bold">
          {success ? "Registration Successful!" : "Payment Failed!"}
        </Typography>
      </Box>

      {/* Payment Details */}
      {(typeof amount === "number" || details) && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            textAlign: "left",
            borderRadius: 3,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Payment Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={1}>
            {typeof amount === "number" && (
              <>
                <Grid item xs={6}>
                  <Typography fontWeight="medium">Amount</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="success.main" fontWeight="bold">
                    ₹{amount}
                  </Typography>
                </Grid>
              </>
            )}
            {details?.orderId && (
              <>
                <Grid item xs={6}>
                  <Typography fontWeight="medium">Order ID</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>{details.orderId}</Typography>
                </Grid>
              </>
            )}
            {details?.referenceId && (
              <>
                <Grid item xs={6}>
                  <Typography fontWeight="medium">Reference ID</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>{details.referenceId}</Typography>
                </Grid>
              </>
            )}
            {details?.status && (
              <>
                <Grid item xs={6}>
                  <Typography fontWeight="medium">Status</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color:
                        details.status === "SUCCESS"
                          ? "success.main"
                          : "warning.main",
                    }}
                  >
                    {details.status}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      )}

      {/* Website Redirect Button */}
      <Box sx={{ textAlign: "center", my: 1 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<OpenInNew />}
          href="https://cyclecircle.one"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            borderRadius: 3,
            fontWeight: "bold",
            textTransform: "none",
            px: 3,
            py: 1.2,
            boxShadow: 3,
            backgroundColor: "var(--color-blue)",
            "&:hover": {
              backgroundColor: "var(--color-blue-hover)",
            },
          }}
        >
          Visit CycleCircle.one
        </Button>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, fontSize: "0.85rem" }}
        >
          Shop premium bicycle parts, accessories, and maintenance products.
        </Typography>
      </Box>

      {/* User Registration Details */}
      {userData && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            textAlign: "left",
            borderRadius: 3,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Registration Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography fontWeight="medium">Name</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {userData.firstName} {userData.lastName || ""}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="medium">Email</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {userData.email}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="medium">Contact</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{userData.primaryContact}</Typography>
            </Grid>

            {userData.dob && (
              <>
                <Grid item xs={6}>
                  <Typography fontWeight="medium">DOB</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>{userData.dob}</Typography>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <Typography fontWeight="medium">Participant Type</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ textTransform: "capitalize" }}>
                {userData.participationType}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}
